import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import {
  Input,
  Select,
  Button,
  Modal,
  DataTable,
  showToast,
  ConfirmDialog
} from '../components/UI';
import itemService from '../services/itemService';
import { Item, ItemFormData, ItemType } from '../types/item';

interface ItemFormDataWithEdit extends ItemFormData {
  isEdit?: boolean;
}

interface TableParams {
  page: number;
  limit: number;
  search: string;
}

// Validation schema
const itemSchema: yup.ObjectSchema<ItemFormDataWithEdit> = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(1, 'Name cannot be empty')
    .max(100, 'Name must be less than 100 characters')
    .matches(/^[a-zA-Z0-9\s\-_."&()/]+$/, 'Name can only contain letters, numbers, spaces, and common symbols'),
  rate: yup
    .number()
    .required('Rate is required')
    .min(0, 'Rate must be 0 or greater')
    .max(999999.99, 'Rate cannot exceed 999,999.99'),
  type: yup
    .mixed<ItemType>()
    .oneOf(['PVC', 'PIPELINE', 'LABOUR', 'TRANSPORT', 'OTHER'])
    .required('Type is required'),
  purchase_rate: yup
    .number()
    .transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value)
    .when('type', {
      is: 'PVC',
      then: (schema) => schema.required('Purchase rate is required for PVC type').min(0, 'Purchase rate must be 0 or greater'),
      otherwise: (schema) => schema.min(0, 'Purchase rate must be 0 or greater').optional()
    }),
  inch: yup
    .number()
    .transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value)
    .when('type', {
      is: 'PVC',
      then: (schema) => schema.required('Inch is required for PVC type').min(0.01, 'Inch must be greater than 0'),
      otherwise: (schema) => schema.min(0, 'Inch must be 0 or greater').optional()
    }),
  kg: yup
    .number()
    .transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value)
    .when('type', {
      is: 'PVC',
      then: (schema) => schema.required('Kg is required for PVC type').min(0, 'Kg must be 0 or greater'),
      otherwise: (schema) => schema.min(0, 'Kg must be 0 or greater').optional()
    }),
  isEdit: yup.boolean().optional(),
});

const ItemManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; item: Item | null }>({
    show: false,
    item: null
  });
  const [deleting, setDeleting] = useState(false);
  
  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 10,
    search: ''
  });
  
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  
  const loadingRef = useRef(false);

  // Debounce search
  const debouncedSearch = useDebounce(searchInput, 500);

  // Form handling
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch
  } = useForm<ItemFormDataWithEdit>({
    resolver: yupResolver(itemSchema),
    defaultValues: {
      name: '',
      rate: 0,
      type: 'OTHER',
      isEdit: false
    }
  });

  // Watch type field to determine if PVC fields are required
  const selectedType = watch('type');
  
  // Submission protection
  const { protectedSubmit } = useSubmissionProtection();

  // Load items with pagination and filters
  const loadItems = useCallback(async (params: TableParams) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    try {
      setLoading(true);
      
      const response = await itemService.getItems({
        page: params.page,
        limit: params.limit,
        search: params.search
      });
      
      setItems(response.data || []);
      setTotalItems(response.pagination?.total || 0);
      setTotalPages(response.pagination?.pages || 1);
    } catch (error: any) {
      console.error('Error loading items:', error);
      showToast.error(error.response?.data?.message || 'Failed to load items');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Effect for role-based access control - Admin only
  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      showToast.error('Only administrators can access Item management');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Effect for loading data when params change
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadItems(tableParams);
    }
  }, [tableParams, loadItems, user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePageChange = (page: number) => {
    setTableParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setTableParams(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleCreate = () => {
    reset({
      name: '',
      rate: 0,
      type: 'OTHER',
      isEdit: false
    });
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = useCallback((item: Item) => {
    reset({
      name: item.name,
      rate: item.rate,
      purchase_rate: item.purchase_rate || undefined,
      inch: item.inch || undefined,
      kg: item.kg || undefined,
      type: item.type,
      isEdit: true
    });
    setEditingItem(item);
    setShowModal(true);
  }, [reset]);

  const handleDelete = (item: Item) => {
    setDeleteConfirm({ show: true, item });
    setShowActionMenu(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.item) return;
    
    try {
      setDeleting(true);
      await itemService.deleteItem(deleteConfirm.item.id);
      showToast.success('Item deleted successfully');
      setDeleteConfirm({ show: false, item: null });
      loadItems(tableParams);
    } catch (error: any) {
      console.error('Error deleting item:', error);
      showToast.error(error.response?.data?.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    reset();
  };

  const onSubmit = async (data: ItemFormDataWithEdit) => {
    const submitFunction = async (formData: ItemFormDataWithEdit) => {
      setSubmitting(true);
      try {
        const { isEdit, ...itemData } = formData;
        
        if (editingItem) {
          await itemService.updateItem(editingItem.id, itemData);
          showToast.success('Item updated successfully');
        } else {
          await itemService.createItem(itemData);
          showToast.success('Item created successfully');
        }
        
        handleCloseModal();
        loadItems(tableParams);
      } finally {
        setSubmitting(false);
      }
    };

    try {
      await protectedSubmit(data, submitFunction);
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.keys(backendErrors).forEach((field) => {
          setError(field as any, {
            type: 'server',
            message: backendErrors[field]
          });
        });
      } else {
        showToast.error(error.response?.data?.message || error.message || 'Failed to save item');
      }
      
      setSubmitting(false);
    }
  };

  // Helper functions
  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString()}`;
  };

  // Table columns
  const columns = useMemo<ColumnDef<Item>[]>(() => [
    {
      id: 'sno',
      header: 'S.No',
      cell: ({ row }) => (
        <div className="text-center font-medium">
          {((tableParams.page - 1) * tableParams.limit) + row.index + 1}
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="font-medium text-gray-900">{row.original.name}</div>
      ),
    },
    {
      accessorKey: 'rate',
      header: 'Rate',
      cell: ({ row }) => (
        <div className="font-medium text-green-600">
          {formatCurrency(row.original.rate)}
        </div>
      ),
    },
    {
      accessorKey: 'purchase_rate',
      header: 'Purchase Rate',
      cell: ({ row }) => (
        <div className="font-medium text-gray-700">
          {row.original.purchase_rate ? formatCurrency(row.original.purchase_rate) : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'inch',
      header: 'Inch',
      cell: ({ row }) => (
        <div className="font-medium text-gray-700">
          {row.original.inch ? row.original.inch : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'kg',
      header: 'Kg',
      cell: ({ row }) => (
        <div className="font-medium text-gray-700">
          {row.original.kg ? row.original.kg : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const typeColors: Record<ItemType, string> = {
          PVC: 'bg-blue-100 text-blue-800',
          PIPELINE: 'bg-purple-100 text-purple-800',
          LABOUR: 'bg-orange-100 text-orange-800',
          TRANSPORT: 'bg-green-100 text-green-800',
          OTHER: 'bg-gray-100 text-gray-800'
        };

        return (
          <div className="flex items-center">
            <span className={`px-2 py-1 text-xs font-medium rounded ${typeColors[row.original.type]}`}>
              {row.original.type}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {user?.role === 'ADMIN' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row.original)}
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(row.original)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ], [user?.role, tableParams.page, tableParams.limit, handleEdit]);

  // Check for authorization before rendering - Admin only
  if (user && user.role !== 'ADMIN') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Admin Access Only</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">Only Administrator can manage Items.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Item Management</h1>
          <div className="flex items-center gap-4">
            {user?.role === 'ADMIN' && (
              <Button onClick={handleCreate} className="flex items-center gap-2">
                <PlusIcon className="w-5 h-5" />
                Add Item
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="max-w-md">
            <Input
              placeholder="Search items..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Data Table for Desktop */}
        {!isMobile ? (
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col">
            <DataTable
              data={items}
              columns={columns}
              loading={loading}
              searchable={false}
              pageSize={tableParams.limit}
              showPagination={false}
              sorting={sorting}
              onSortingChange={setSorting}
            />
            
            {/* Custom Pagination */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-gray-700 whitespace-nowrap">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalItems)} of{' '}
                  {totalItems} results
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="w-full sm:w-auto">
                    <Select
                      options={[
                        { value: '5', label: '5 per page' },
                        { value: '10', label: '10 per page' },
                        { value: '20', label: '20 per page' },
                        { value: '50', label: '50 per page' }
                      ]}
                      value={{ value: tableParams.limit.toString(), label: `${tableParams.limit} per page` }}
                      onChange={(option: any) => handlePageSizeChange(Number(option?.value))}
                      usePortal={false}
                      menuPlacement="top"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page - 1)}
                      disabled={tableParams.page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg min-w-[120px] justify-center">
                      Page {tableParams.page} of {totalPages}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page + 1)}
                      disabled={tableParams.page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading items...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-medium border border-gray-200 p-4">
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <CubeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No items found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl shadow-medium border border-gray-200 p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-lg">{item.name}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            Rate: <span className="font-medium text-green-600">{formatCurrency(item.rate)}</span>
                          </div>
                          <div className="mt-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              item.type === 'PVC' ? 'bg-blue-100 text-blue-800' :
                              item.type === 'PIPELINE' ? 'bg-purple-100 text-purple-800' :
                              item.type === 'LABOUR' ? 'bg-orange-100 text-orange-800' :
                              item.type === 'TRANSPORT' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.type}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            Created: {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        
                        {user?.role === 'ADMIN' && (
                          <div className="relative">
                            <button
                              onClick={() => setShowActionMenu(showActionMenu === item.id ? null : item.id)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
                            </button>
                            
                            {showActionMenu === item.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                <button
                                  onClick={() => {
                                    handleEdit(item);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                >
                                  <PencilIcon className="w-4 h-4 mr-2" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    handleDelete(item);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                >
                                  <TrashIcon className="w-4 h-4 mr-2" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Pagination */}
            <div className="flex-shrink-0 bg-white rounded-2xl shadow-medium border border-gray-200 p-4">
              <div className="flex flex-col gap-4">
                <div className="text-sm text-gray-700 text-center">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalItems)} of{' '}
                  {totalItems} results
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page - 1)}
                    disabled={tableParams.page === 1}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg">
                    Page {tableParams.page} of {totalPages}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page + 1)}
                    disabled={tableParams.page === totalPages}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingItem ? 'Edit Item' : 'Add New Item'}
        >
          <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Item Type *"
                  value={field.value ? { value: field.value, label: field.value } : null}
                  onChange={(option: any) => field.onChange(option?.value || 'OTHER')}
                  error={errors.type?.message}
                  isDisabled={submitting}
                  options={[
                    { value: 'PVC', label: 'PVC' },
                    { value: 'PIPELINE', label: 'PIPELINE' },
                    { value: 'LABOUR', label: 'LABOUR' },
                    { value: 'TRANSPORT', label: 'TRANSPORT' },
                    { value: 'OTHER', label: 'OTHER' }
                  ]}
                />
              )}
            />

            <Input
              label="Name *"
              {...register('name')}
              error={errors.name?.message}
              placeholder="Enter item name"
              disabled={submitting}
            />

            <Input
              label="Rate (₹) *"
              type="number"
              step="0.01"
              min="0"
              {...register('rate', { valueAsNumber: true })}
              error={errors.rate?.message}
              placeholder="Enter rate"
              disabled={submitting}
            />

            <Input
              label={selectedType === 'PVC' ? 'Purchase Rate (₹) *' : 'Purchase Rate (₹)'}
              type="number"
              step="0.01"
              min="0"
              {...register('purchase_rate', { valueAsNumber: true })}
              error={errors.purchase_rate?.message}
              placeholder={selectedType === 'PVC' ? 'Enter purchase rate (required for PVC)' : 'Enter purchase rate (optional)'}
              disabled={submitting}
            />

            <Input
              label={selectedType === 'PVC' ? 'Inch *' : 'Inch'}
              type="number"
              step="0.01"
              min="0"
              {...register('inch', { valueAsNumber: true })}
              error={errors.inch?.message}
              placeholder={selectedType === 'PVC' ? 'Enter inch (required for PVC)' : 'Enter inch (optional)'}
              disabled={submitting}
            />

            <Input
              label={selectedType === 'PVC' ? 'Kg *' : 'Kg'}
              type="number"
              step="0.01"
              min="0"
              {...register('kg', { valueAsNumber: true })}
              error={errors.kg?.message}
              placeholder={selectedType === 'PVC' ? 'Enter kg (required for PVC)' : 'Enter kg (optional)'}
              disabled={submitting}
            />

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, item: null })}
          onConfirm={confirmDelete}
          title="Delete Item"
          message={`Are you sure you want to delete the item "${deleteConfirm.item?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleting}
        />
      </div>
    </MainLayout>
  );
};

export default ItemManagement;